import { useMemo } from 'react'
import { Currency, Percent, Trade, TradeType } from '@pancakeswap/sdk'
import { BigNumber } from '@ethersproject/bignumber'
import IPancakeRouter02ABI from 'config/abi/IPancakeRouter02.json'
import { useTransactionAdder } from 'state/transactions/hooks'
import { calculateGasMargin } from 'utils'
import useActiveWeb3React from './useActiveWeb3React'
import { useContract } from './useContract'
import { QOMX_ROUTER_ADDRESS, QOMX_WETH_ADDRESS } from '../config/constants/qomx'
import { wrappedCurrency } from '../utils/wrappedCurrency'

enum SwapCallbackState {
  INVALID = 'INVALID',
  LOADING = 'LOADING',
  VALID = 'VALID',
}

/**
 * Returns a function that executes a swap of Q via QOMX router.
 * Allowed slippage in BIPS (e.g. 50 = 0.5%).
 * inputIsNative/outputIsNative passed by caller since the trade has WQOM tokens internally.
 */
export function useQOMXSwapCallback(
  trade: Trade<Currency, Currency, TradeType> | null | undefined,
  allowedSlippage: number,
  recipient?: string | null,
  inputIsNative: boolean = false,
  outputIsNative: boolean = false,
): { state: SwapCallbackState; callback?: () => Promise<string>; error?: string } {
  const { account, chainId } = useActiveWeb3React()
  const router = useContract(QOMX_ROUTER_ADDRESS, IPancakeRouter02ABI, true)
  const addTransaction = useTransactionAdder()

  return useMemo(() => {
    if (!trade || !router || !account || !chainId) {
      return { state: SwapCallbackState.INVALID, error: 'Missing dependencies' }
    }

    const recipientAddress = recipient ?? account
    const tokenIn = wrappedCurrency(trade.inputAmount.currency, chainId)
    const tokenOut = wrappedCurrency(trade.outputAmount.currency, chainId)
    if (!tokenIn || !tokenOut) {
      return { state: SwapCallbackState.INVALID, error: 'Invalid tokens' }
    }

    // Build path. Path uses QOMX's WQOM (different from QomSwap's WQOM)
    const path = trade.route.path.map((t) =>
      t.symbol === 'WQOM' ? QOMX_WETH_ADDRESS : t.address,
    )

    const isExactIn = trade.tradeType === TradeType.EXACT_INPUT

    const slippagePercent = new Percent(JSON.stringify(allowedSlippage), '10000')
    const minimumAmountOut = trade.minimumAmountOut(slippagePercent)
    const maximumAmountIn = trade.maximumAmountIn(slippagePercent)

    const deadline = Math.floor(Date.now() / 1000) + 60 * 20

    return {
      state: SwapCallbackState.VALID,
      callback: async (): Promise<string> => {
        let methodName: string
        let args: any[]
        let value: BigNumber = BigNumber.from(0)

        if (isExactIn) {
          if (inputIsNative) {
            methodName = 'swapExactETHForTokens'
            args = [
              `0x${minimumAmountOut.quotient.toString(16)}`,
              path,
              recipientAddress,
              deadline,
            ]
            value = BigNumber.from(`0x${trade.inputAmount.quotient.toString(16)}`)
          } else if (outputIsNative) {
            methodName = 'swapExactTokensForETH'
            args = [
              `0x${trade.inputAmount.quotient.toString(16)}`,
              `0x${minimumAmountOut.quotient.toString(16)}`,
              path,
              recipientAddress,
              deadline,
            ]
          } else {
            methodName = 'swapExactTokensForTokens'
            args = [
              `0x${trade.inputAmount.quotient.toString(16)}`,
              `0x${minimumAmountOut.quotient.toString(16)}`,
              path,
              recipientAddress,
              deadline,
            ]
          }
        } else {
          if (inputIsNative) {
            methodName = 'swapETHForExactTokens'
            args = [
              `0x${trade.outputAmount.quotient.toString(16)}`,
              path,
              recipientAddress,
              deadline,
            ]
            value = BigNumber.from(`0x${maximumAmountIn.quotient.toString(16)}`)
          } else if (outputIsNative) {
            methodName = 'swapTokensForExactETH'
            args = [
              `0x${trade.outputAmount.quotient.toString(16)}`,
              `0x${maximumAmountIn.quotient.toString(16)}`,
              path,
              recipientAddress,
              deadline,
            ]
          } else {
            methodName = 'swapTokensForExactTokens'
            args = [
              `0x${trade.outputAmount.quotient.toString(16)}`,
              `0x${maximumAmountIn.quotient.toString(16)}`,
              path,
              recipientAddress,
              deadline,
            ]
          }
        }

        const gasEstimate = await router.estimateGas[methodName](...args, { value })
        const tx = await router[methodName](...args, {
          value,
          gasLimit: calculateGasMargin(gasEstimate),
        })

        addTransaction(tx, {
          summary: `Swap ${trade.inputAmount.toSignificant(6)} ${
            trade.inputAmount.currency.symbol
          } for ${trade.outputAmount.toSignificant(6)} ${trade.outputAmount.currency.symbol} (via QOMX)`,
        })

        return tx.hash
      },
    }
  }, [trade, router, account, chainId, recipient, allowedSlippage, addTransaction])
}

export { SwapCallbackState }
